/**
 * Request Health Indicator
 *
 * Health status badge for a collection request, showing
 * on-track / stalled / blocked status.
 */

import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

interface RequestHealthIndicatorProps {
  healthStatus: string
}

interface HealthConfig {
  bg: string
  text: string
  dot: string
  label: string
}

function getHealthConfig(
  healthStatus: string,
  t: (
    key:
      | 'collections.targetMonitoring.healthOnTrack'
      | 'collections.targetMonitoring.healthStalled'
      | 'collections.targetMonitoring.healthBlocked'
  ) => string
): HealthConfig {
  switch (healthStatus) {
    case 'stalled':
      return {
        bg: 'bg-orange-100 dark:bg-orange-950/50',
        text: 'text-orange-700 dark:text-orange-400',
        dot: 'bg-orange-500',
        label: t('collections.targetMonitoring.healthStalled'),
      }
    case 'blocked':
      return {
        bg: 'bg-red-100 dark:bg-red-950/50',
        text: 'text-red-700 dark:text-red-400',
        dot: 'bg-red-500',
        label: t('collections.targetMonitoring.healthBlocked'),
      }
    default:
      return {
        bg: 'bg-green-100 dark:bg-green-950/50',
        text: 'text-green-700 dark:text-green-400',
        dot: 'bg-green-500',
        label: t('collections.targetMonitoring.healthOnTrack'),
      }
  }
}

export function RequestHealthIndicator({
  healthStatus,
}: RequestHealthIndicatorProps): ReactElement {
  const { t } = useTranslation('cases')

  const config = getHealthConfig(healthStatus, t)

  return (
    <span
      className={`items-centre inline-flex gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${config.bg} ${config.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  )
}
